import React from 'react'
import classnames from 'classnames'
import { Dialog, Button, Text, Flex, Content, Pane } from '@leverege/ui-elements'

import S from './EditModel.css'

/**
 * @class EditModel — A component for editing an oidc client
 * @component 
 */
export default class EditModel extends React.Component {

  static defaultProps = {
    deleteMessage : 'This will remove this item.',
    deleteTitle : 'Delete?',
    deleteButton : 'Delete',
    variants : {
      pane : 'form',
      header : 'formHeader',
      body : 'formBody',
      title : 'formTitle',
      footer : 'formFooter'
    }
  }

  constructor( props ) {
    super( props )
    this.state = { value : props.value, original : props.value, showDelete : false }
  }

  static getDerivedStateFromProps( props, state ) {
    if ( props.value ) {
      if ( state.original == null || state.original != props.value ) {
        return { value : props.value, original : props.value }
      }
    } else {
      return { value : null, original : null }
    }
    return null
  }

  componentDidMount( ) {
    const { original, value } = this.state 
    const { history, saveOnChange } = this.props

    if ( history && saveOnChange !== true ) {
      this.unblock = history.block( () => { // eslint-disable-line consistent-return 
        if ( original && original !== value ) {
          return 'Your changes will be lost. Are you sure your want to continue?'
        }

      } )
    }
    window.addEventListener( 'beforeunload', this.onWindowUnload )
  }

  componentWillUnmount() {
    window.removeEventListener( 'beforeunload', this.onWindowUnload )

    if ( this.unblock ) {
      this.unblock()
    }
  }

  /**
   * @method onWindowUnload Clean up as the component unmounts 
   * @param {object} evt  
   */
  onWindowUnload = ( evt ) => {
    const { original, value } = this.state 
    if ( original && original !== value ) {
      evt.preventDefault()
      evt.returnValue = true // eslint-disable-line no-param-reassign 
    }
  }

  /**
   * @method onChange Process and change event
   * @param {object} evt 
   */
  onChange = ( evt ) => {
    const { saveOnChange, onSave } = this.props 

    if ( saveOnChange ) {
      const { original : oldValue, eventData : data } = this.state
      onSave( { value : evt.value, oldValue, data } )
    }
    this.setState( { value : evt.value } )
  }

  /**
     * @method onSave Save changes to the oidc client
     * @param {object} evt 
     */
  onSave = ( evt ) => {
    const { eventData : data, onSave } = this.props
    const { original : oldValue, value } = this.state

    onSave( { value, oldValue, data } )
  }

  /**
   * @method onDelete Show a deletion dialog
   * @param {object} item 
   */
  onDelete = ( item ) => {
    this.setState( { showDelete : true } )
  }

  /**
   * @method onDoDelete Unblock switch urls and show a deletion dialog
   * @param {object} event 
   */
  onDoDelete = ( event ) => {
    const { value, onDelete } = this.props
    this.setState( { showDelete : false } )
    if ( this.unblock ) {
      this.unblock() // remove blocks from switch urls
    }
    onDelete( value )
  }

  /**
   * @method onCancelDelete Hide the delete dialog
   * @param {object} event 
   */
  onCancelDelete = ( event ) => {
    this.setState( { showDelete : false } )
  }

  /**
   * @method onCancel Cancel the editing state 
   * @param {object} event 
   */
  onCancel = ( event ) => {
    const { eventData : data, onCancel, value } = this.props
    if ( onCancel ) {
      onCancel( { data } )
    }
    this.setState( { value } )
  }

  render() {
    const { title, deleteMessage, deleteTitle, deleteButton, saveOnChange, validator,
            actionState = null, children, cancelLabel = 'Cancel', okayLabel = 'Save', 
            deleteLabel = 'Delete', className, editor : Editor, variants : v, 
            noModelView = null, onDelete, onCancel, editorProps = {} } = this.props
    const { value, showDelete, original } = this.state

    const disabled = (
      value === original ||
      actionState === 'saving' ||
      actionState === 'deleting' ||
      ( validator != null && !validator( value, original ) )
    )

    if ( value == null ) {
      return (
        <Pane variant={v.pane} className={classnames( className, S.form )}>
          {noModelView}
        </Pane>
      )
    }

    return (
      <Pane variant={v.pane} className={classnames( className, S.form )}>
        <Content relative className={S.content}>
          <Content.Header variant={v.header} className={S.header}>
            <Flex variant="row" justify="space-between" align="center">
              {resolveTitle( title, value, original, v.title )}
            </Flex>
          </Content.Header>
          <Content.Area variant={v.body} className={S.area}>
            <Editor {...editorProps} value={value} onChange={this.onChange}/>
            {React.Children.count( children ) > 0 ? <div className={S.spacer}/> : null }
            {children}
          </Content.Area>
          <Content.Footer variant={v.footer} className={S.footer}>
            { onDelete == null ? <div/> : (
              <Button className={S.delete} variant="linkDestructive" onClick={this.onDelete}>{deleteLabel}</Button>
            )}
            { saveOnChange !== true && (
              <div className={S.saveCancel}>
                <Button variant="secondary" onClick={this.onCancel} disabled={onCancel == null && disabled}>{cancelLabel}</Button>
                <Button variant="primary" onClick={this.onSave} disabled={disabled}>
                  {actionState === 'saving' ? <i className="fa fa-spin fa-spinner"/> : okayLabel }
                </Button>
              </div>
            )}
            { saveOnChange === true && ( actionState === 'saving' || actionState === 'deleting' ) && (
              <div className={S.saveCancel}>
                <i className="fa fa-spin fa-spinner"/>
              </div>
            )}
            <Dialog.Question
              className={S.dialog}
              show={showDelete}
              title={resolve( deleteTitle, value, original )}
              message={resolve( deleteMessage, value, original )}
              okay={resolve( deleteButton, value, original )}
              okayVariant="primaryDestructive"
              onCancel={this.onCancelDelete}
              onOkay={this.onDoDelete}/>

          </Content.Footer>
        </Content>
      </Pane>
    )
  }
}

/**
 * @function resolveTitle If `funcOrString` is a function, call it with the rest of the arguments, 
 * otherwise, return it as a string wrapped in a <Text /> element.
 * @param {function|string} funcOrString 
 * @param {*} value 
 * @param {*} original 
 * @param {*} variant 
 */
function resolveTitle( funcOrString, value, original, variant ) {
  const resolvedValue = resolve( funcOrString, value, original )
  if ( typeof resolvedValue === 'string' ) {
    console.log(variant)
    return <Text variant={variant}>{resolvedValue}</Text>
  }
  return resolvedValue
}

/**
 * @function resolve Call `funcOrString` with the rest of the arguments if its a function, 
 * otherwise, return it (as a string)
 * @param {function|string} funcOrString 
 * @param {string} variant 
 * @param {any} value 
 * @param {any} original 
 * @return {any|string} Returns the result of `funcOrString`, or that same argument as a string. 
 */
function resolve( funcOrString, value, original, variant ) {
  if ( typeof funcOrString === 'function' ) {
    return funcOrString( value, original, variant )
  }
  return funcOrString
}
